import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { CircularProgress, Collapse, IconButton, InputBase, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useState } from 'react';
import { useMatch } from 'react-router';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { CreateListDialog } from '../../../../../pages/Sourcing/CreateListDialog';
import { ConfirmDeletionDialog } from '../../../../Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { RefreshSettingsDialog } from '../../../../../pages/Sourcing/RefreshSettingsDialog';
import { NavigationWrapper } from '../NavigationWrapper';
import { SavedSearchItem } from './SavedSearchItem';
import { SourcingListItem } from './SourcingListItem';
import { useSourcingNavigation } from './useSourcingNavigation';

// ─── Styled components ───────────────────────────────────────────────────────

const StyledSearchInput = styled(InputBase)`
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 0.8px solid ${({ theme }) => theme.colors.primary[10]};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary[10]};
  font-size: 0.875rem;
  margin-bottom: 4px;
  &:hover {
    border-color: #b89eff;
  }
  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.colors.primary[10]};
    opacity: 0.7;
  }
`;

const SectionHeader = styled('div')`
  height: 36px;
  display: flex;
  align-items: center;
  padding: 4px 0;
  cursor: pointer;
`;

const SectionHeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const ItemsList = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
`;

// ─── ListsSectionHeader ───────────────────────────────────────────────────────

interface ListsSectionHeaderProps {
  expanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
}

const ListsSectionHeader: FC<ListsSectionHeaderProps> = ({ expanded, onToggle, onAdd }) => {
  const { secondary } = useTheme().colors;
  const [isHovered, setIsHovered] = useState(false);

  return (
    <SectionHeader
      onClick={onToggle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SectionHeaderLeft>
        <Typography variant='subtitle2' color={secondary[10]} style={{ textTransform: 'uppercase', flex: 1 }}>
          Lists
        </Typography>
        <IconButton
          size='small'
          sx={{ color: secondary[10], p: '2px', opacity: isHovered ? 1 : 0, transition: 'opacity 0.15s' }}
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </SectionHeaderLeft>
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          color: secondary[10],
          transition: 'transform .1s',
          transform: expanded ? '' : 'rotate(-90deg)',
          flexShrink: 0,
        }}
      />
    </SectionHeader>
  );
};

// ─── SavedSearchesSectionHeader ───────────────────────────────────────────────

interface SavedSearchesSectionHeaderProps {
  expanded: boolean;
  onToggle: () => void;
}

const SavedSearchesSectionHeader: FC<SavedSearchesSectionHeaderProps> = ({ expanded, onToggle }) => {
  const { secondary } = useTheme().colors;

  return (
    <SectionHeader onClick={onToggle}>
      <SectionHeaderLeft>
        <Typography variant='subtitle2' color={secondary[10]} style={{ textTransform: 'uppercase', flex: 1 }}>
          Saved Searches
        </Typography>
      </SectionHeaderLeft>
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          color: secondary[10],
          transition: 'transform .1s',
          transform: expanded ? '' : 'rotate(-90deg)',
          flexShrink: 0,
        }}
      />
    </SectionHeader>
  );
};

// ─── SourcingNavigation ───────────────────────────────────────────────────────

export const SourcingNavigation = () => {
  const { secondary } = useTheme().colors;
  const match = useMatch(`/${ROUTES.SOURCING}/*`);
  const {
    loading,
    filteredLists,
    filteredSavedSearches,
    showSearch,
    searchQuery,
    setSearchQuery,
    listsExpanded,
    setListsExpanded,
    savedSearchesExpanded,
    setSavedSearchesExpanded,
    createDialogOpen,
    setCreateDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deleteTargetRef,
    handleCreateList,
    isCreating,
    handleRenameList,
    handleDeleteListConfirm,
    isDeleting,
  } = useSourcingNavigation();

  if (match === null) {
    return null;
  }

  return (
    <NavigationWrapper title='Sourcing'>
      {loading ? (
        <CircularProgress size={20} sx={{ color: 'white', alignSelf: 'center' }} />
      ) : (
        <>
          {showSearch && (
            <StyledSearchInput
              startAdornment={<SearchIcon sx={{ fontSize: 20, color: 'inherit', mr: '8px' }} />}
              placeholder='Search'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          )}

          <div>
            <ListsSectionHeader
              expanded={listsExpanded}
              onToggle={() => setListsExpanded((prev) => !prev)}
              onAdd={() => {
                setListsExpanded(true);
                setCreateDialogOpen(true);
              }}
            />
            <Collapse in={listsExpanded}>
              <ItemsList>
                {filteredLists.length === 0 && (
                  <Typography variant='body2' sx={{ color: secondary[20], px: 1, py: 0.5 }}>
                    {searchQuery ? 'No matching lists' : 'No lists yet'}
                  </Typography>
                )}
                {filteredLists.map((list) => (
                  <SourcingListItem
                    key={list.id}
                    item={list}
                    onDelete={(item) => {
                      deleteTargetRef.current = item;
                      setDeleteDialogOpen(true);
                    }}
                    onRename={handleRenameList}
                  />
                ))}
              </ItemsList>
            </Collapse>
          </div>

          <div>
            <SavedSearchesSectionHeader
              expanded={savedSearchesExpanded}
              onToggle={() => setSavedSearchesExpanded((prev) => !prev)}
            />
            <Collapse in={savedSearchesExpanded}>
              <ItemsList>
                {filteredSavedSearches.length === 0 && (
                  <Typography variant='body2' sx={{ color: secondary[20], px: 1, py: 0.5 }}>
                    {searchQuery ? 'No matching saved searches' : 'No saved searches yet'}
                  </Typography>
                )}
                {filteredSavedSearches.map((item) => (
                  <SavedSearchItem key={item.id} item={item} />
                ))}
              </ItemsList>
            </Collapse>
          </div>
        </>
      )}
      <CreateListDialog
        open={createDialogOpen}
        onCancel={() => setCreateDialogOpen(false)}
        loading={isCreating}
        onConfirm={handleCreateList}
      />
      <ConfirmDeletionDialog
        open={deleteDialogOpen}
        title={`Delete "${deleteTargetRef.current?.name ?? ''}"?`}
        loading={isDeleting}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteListConfirm}
      >
        The list will be deleted permanently.
      </ConfirmDeletionDialog>
      <RefreshSettingsDialog />
    </NavigationWrapper>
  );
};
